import { messageKey, type MessageKey } from "@baton/i18n";
import { useEffect } from "react";

/**
 * FR-015-023, FR-015-100, `contracts/design-system.md` § Keyboard § Global: the shortcut
 * map, suppressed while focus is in a text input or an open dialog except the modifier combinations, `Cmd`
 * reading as `Ctrl` off macOS, and every shortcut also reachable as a menu item or a
 * button. A row is registered only when its target exists in this build, so the reference
 * dialog lists what works and nothing that does not (constitution § IV).
 */
export interface Shortcut {
  readonly id: string;
  /** As the design-system table writes it: "Mod+K", "g m", "?", "[". */
  readonly keys: string;
  readonly label: MessageKey;
  readonly run: () => void;
  /** True for a combination that works even while typing in a field. */
  readonly modifier?: boolean;
}

/**
 * The sections the reference dialog groups its rows under, in the order it draws them
 * (2026-09-21, by the owner's decision: one scrolling list of fifty rows was unreadable).
 * A row names its own section, so a shortcut added anywhere lands in the right group rather
 * than being sorted by a rule that reads its identifier.
 */
export const SHORTCUT_SECTIONS = ["general", "goto", "list", "selection", "create", "edit", "act"] as const;

export type ShortcutSection = (typeof SHORTCUT_SECTIONS)[number];

/**
 * What the reference dialog lists of a shortcut: its keys, its name and the section it belongs to, and
 * for a run of keys that each do the same thing to their own target, the last key of the run, drawn as
 * "1 to 9" (2026-10-13).
 */
export type ShortcutRow = Pick<Shortcut, "id" | "keys" | "label"> & { readonly section: ShortcutSection; readonly through?: string };

/**
 * The global map of `contracts/design-system.md` § Keyboard § Global as this build registers it, in
 * the order the reference dialog lists it. The workspace shell attaches each row's action; Preferences
 * renders the same rows in its reference dialog, so the two never list different maps. `?` opens the
 * reference as well and is registered by the shell beside its `Mod+/` row rather than listed twice.
 */
export const GLOBAL_SHORTCUT_ROWS = [
  { id: "palette", keys: "Mod+K", label: messageKey("shortcuts.palette"), section: "general" },
  { id: "reference", keys: "Mod+/", label: messageKey("shortcuts.reference"), section: "general" },
  { id: "quick-create", keys: "n", label: messageKey("shortcuts.quickCreate"), section: "create" },
  { id: "go-inbox", keys: "g i", label: messageKey("shortcuts.goInbox"), section: "goto" },
  { id: "go-my-work", keys: "g m", label: messageKey("shortcuts.goMyWork"), section: "goto" },
  { id: "go-search", keys: "g s", label: messageKey("shortcuts.goSearch"), section: "goto" },
  { id: "go-projects", keys: "g p", label: messageKey("shortcuts.goProjects"), section: "goto" },
  { id: "sidebar", keys: "[", label: messageKey("shortcuts.sidebar"), section: "general" },
  { id: "peek", keys: "Mod+\\", label: messageKey("shortcuts.peek"), section: "general" },
  // 019 FR-015-180 (2026-10-14): the assistant panel, from any screen of the Team and from inside the panel's composer.
  { id: "assistant", keys: "Mod+J", label: messageKey("shortcuts.assistant"), section: "general" },
  { id: "theme", keys: "Mod+Shift+L", label: messageKey("shortcuts.theme"), section: "general" },
] as const satisfies readonly ShortcutRow[];

export type GlobalShortcutId = (typeof GLOBAL_SHORTCUT_ROWS)[number]["id"];

/**
 * The keys of a global row, for the control whose tooltip names them (`contracts/design-system.md`
 * § Key hints, 2026-10-15, FR-015-195): the top bar's search, the plus, the drawer toggle and the
 * inbox bell read their keys here and never write them into their words, so a tooltip, a button and
 * the reference dialog cannot disagree.
 */
export function globalShortcutKeys(id: GlobalShortcutId): string {
  const row = GLOBAL_SHORTCUT_ROWS.find((candidate) => candidate.id === id);
  if (row === undefined) throw new Error(`no global shortcut ${id}`);
  return row.keys;
}

/**
 * 2026-10-13, `contracts/design-system.md` § Keyboard § Tabs: on a page with tabs, `1` to `9` choose the
 * tab of that number, counted from the first; on the client page, whose sections stand side by side
 * rather than behind tabs, the section of that number. The row the reference dialog lists for them;
 * `useTabNumberKeys` is what a page with tabs runs.
 */
export const TAB_NUMBER_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

export const TAB_SHORTCUT_ROWS = [{ id: "tab-number", keys: "1", through: "9", label: messageKey("shortcuts.tabNumber"), section: "goto" }] as const satisfies readonly ShortcutRow[];

/**
 * Run `onSelect` with the index of the tab a number key names, for a page of `count` tabs: a plain digit
 * from `1` to `9` whose tab exists, never while typing in a field or inside an open dialog, popover or menu,
 * never with a modifier, which the browser and the shell keep for themselves, and never inside a task
 * docked beside the page in the side peek, whose own tabs those keys are not.
 */
export function useTabNumberKeys(count: number, onSelect: (index: number) => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isTextEntry(event.target) || isInOverlay(event.target)) return;
      if (event.target instanceof Element && event.target.closest("[data-slot='side-peek']") !== null) return;
      const index = (TAB_NUMBER_KEYS as readonly string[]).indexOf(event.key);
      if (index < 0 || index >= count) return;
      event.preventDefault();
      onSelect(index);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [count, onSelect, enabled]);
}

export const SEQUENCE_WINDOW_MS = 1000;

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "url", "password", "number", "tel"]);

export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(target.type) || target.type.length === 0;
  return target.getAttribute("role") === "textbox" || target.getAttribute("role") === "combobox";
}

/**
 * Whether the key was pressed inside a dialog, a popover or an alert dialog, or inside an open menu or
 * list of options. A modal keeps the focus inside itself, so a plain key pressed while one is open lands
 * here; the shell's plain keys stay out of an open dialog, where `n` would stack New task over a form
 * half filled and `g p` would navigate the page behind it (2026-09-27). A menu and a list of options
 * hold the focus while open too and read a typed character as a search for their own rows, so a plain
 * key there is theirs and not the page's: a `2` typed in an inbox row's menu does not switch the filter
 * behind it (2026-10-13).
 */
export function isInOverlay(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("[role='dialog'], [role='alertdialog'], [role='menu'], [role='listbox']") !== null;
}

/** A parsed chord: modifiers and the key, matched against a keyboard event. */
interface Chord {
  readonly mod: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly key: string;
}

function parseChord(chord: string): Chord {
  const parts = chord.split("+");
  const key = (parts[parts.length - 1] ?? "").toLowerCase();
  return { mod: parts.includes("Mod"), shift: parts.includes("Shift"), alt: parts.includes("Alt"), key };
}

function eventKey(event: KeyboardEvent): string {
  const key = event.key.toLowerCase();
  if (key === "escape") return "esc";
  if (key === " ") return "space";
  return key;
}

/**
 * `Mod` is the command key on macOS and Control elsewhere, and FR-015-004 admits both for
 * the palette, so a `Mod` chord matches either modifier; the reference dialog still shows
 * the platform's own glyph. The other modifier must be up, so `Ctrl+Alt+K` is not `Mod+K`.
 */
export function chordMatches(chord: Chord, event: KeyboardEvent, mac: boolean): boolean {
  const mod = event.metaKey || event.ctrlKey;
  if (chord.mod !== mod) return false;
  if (!chord.mod && (mac ? event.ctrlKey : event.metaKey)) return false;
  if (chord.shift !== event.shiftKey && chord.key !== "?") return false;
  if (chord.alt !== event.altKey) return false;
  return eventKey(event) === chord.key || (chord.key === "\\" && event.key === "\\");
}

export interface KeyboardMapOptions {
  readonly shortcuts: readonly Shortcut[];
  readonly mac: boolean;
  readonly enabled?: boolean;
}

/**
 * Install the map on the document. A two-key sequence ("g m") waits up to one second for
 * its second key; a chord fires at once. Plain keys are ignored while typing and inside an open
 * dialog or popover (`isInOverlay`).
 */
export function useKeyboardMap({ shortcuts, mac, enabled = true }: KeyboardMapOptions): void {
  useEffect(() => {
    if (!enabled) return;
    let pending: { readonly first: string; readonly at: number } | null = null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const plainKeysOff = isTextEntry(event.target) || isInOverlay(event.target);
      const now = Date.now();
      for (const shortcut of shortcuts) {
        const chords = shortcut.keys.split(" ");
        const isModifier = chords.length === 1 && (chords[0] ?? "").includes("+");
        if (plainKeysOff && !isModifier && shortcut.modifier !== true) continue;
        if (chords.length === 1) {
          if (chordMatches(parseChord(chords[0] ?? ""), event, mac)) {
            event.preventDefault();
            pending = null;
            shortcut.run();
            return;
          }
          continue;
        }
        const [first, second] = chords;
        if (first === undefined || second === undefined) continue;
        if (pending !== null && pending.first === first && now - pending.at <= SEQUENCE_WINDOW_MS && chordMatches(parseChord(second), event, mac)) {
          event.preventDefault();
          pending = null;
          shortcut.run();
          return;
        }
      }
      const starters = new Set(shortcuts.filter((s) => s.keys.includes(" ")).map((s) => s.keys.split(" ")[0] ?? ""));
      if (!plainKeysOff && !event.metaKey && !event.ctrlKey && !event.altKey && starters.has(eventKey(event))) {
        pending = { first: eventKey(event), at: now };
        return;
      }
      if (pending !== null && now - pending.at > SEQUENCE_WINDOW_MS) pending = null;
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [shortcuts, mac, enabled]);
}
